'use client';

import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import ProductCard from './ProductCard';
import { ImageAsset, Product } from '@/models/ProductTypes';
import { Log } from '@/logging/datadog';

type ProductCardContainerProps = {
  id: string;
  ids: Array<string>;
  images?: Array<ImageAsset>;
};

const fetcher = async ([url, ids]: [string, Array<string>, string]): Promise<Array<Product>> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ productList: ids }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to fetch product (${res.status}): ${text}`);
  }

  return ((await res.json()) || []) as Product[];
};

const ProductCardContainer: React.FC<ProductCardContainerProps> = ({ id, ids, images }) => {
  const [product, setProduct] = useState<Product | null>();
  const {
    data: products,
    error,
    isLoading,
  } = useSWR(['/api/store/product-list', ids], fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 5000, // Cache and dedupe requests for 5 seconds
    onError: (err) => {
      Log.error('Error in ProductCardContainer', err);
    },
  });

  useEffect(() => {
    if (products) {
      setProduct(products.filter((p) => p.productId === id)[0] || null);
    }
  }, [products, id]);

  if (error || (!isLoading && !product)) {
    return null;
  }

  return (
    <ProductCard
      key={id}
      images={images}
      name={product?.productName || 'Óþekkt vara'}
      group={product?.productGroup || ''}
      category={product?.productCategory || ''}
      price={product?.productPrice ?? 0}
      oldPrice={product?.productDiscountPrice}
      available={true}
      created={product?.productCreatedDate}
    />
  );
};

export default ProductCardContainer;
